/* eslint-disable @typescript-eslint/no-explicit-any */
import { Accordion, AccordionTab } from "primereact/accordion";
import { useHomeContext } from "../../contexts/HomeContext";
import Card from "../Card";
import Text from "../Text";
import Container from "../Container";
import PercentageText from "../PercentageText";
import Currency from "../Currency";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
    AssetFactory,
    AssetMainType,
    IAssetDetailsResponse,
    IAssetProfitEvolution,
    ICashflowModel,
    IFixedIncomeModel,
    IPropertyModel,
    IVariableIncomeModel,
    IVehicleModel,
    residualInvested
} from "../../models/asset";
import { useCallback, useRef, useState } from "react";
import PrivateBondsHandleForm from "../assets/PrivateBondsHandleForm";
import SavingAccountHandleForm from "../assets/SavingAccountHandleForm";
import TreasureBoundsHandleForm from "../assets/TreasureBoundsHandleForm";
import CheckingAccoundHandleForm from "../assets/CheckingAccountHandleForm";
import CustomizedHandleForm from "../assets/CustomizedHandleForm";
import PropertyHandleForm from "../assets/PropertyHandleForm";
import VehicleHandleForm from "../assets/VehicleHandleForm";
import DebentureHandleForm from "../assets/DebentureHandleForm";
import useAuthHttp from "../../hooks/useAuthHttp";
import { confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import CurrencyHandleForm from "../assets/CurrencyHandleForm";
import CriptoHandleForm from "../assets/CriptoHandleForm";
import { Dialog } from "primereact/dialog";
import { Chart } from "primereact/chart";
import moment from "moment";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    assetMainTypeToAnalyticsType,
    trackAssetDeleteCompleted,
    trackAssetDeleteConfirmed,
    trackAssetDeleteStarted,
    trackAssetDetailsViewed,
    trackAssetEditCompleted,
    trackAssetEditStarted,
} from "../../analytics";
import { Dropdown } from "primereact/dropdown";
import Label from "../Label";
import { months } from "../../utils/date";
import { IPropertyDetailsResult } from "../../commons/property";
import { IVehicleResult } from "../../commons/vehicle";
import { Margin, usePDF } from "react-to-pdf";
import FundHandleForm from "../assets/FundHandleForm";
import StockHandleForm from "../assets/StockHandleForm";
import AccordionHeader from "../AccordionHeader";
import AssetAccordionHeaderEndContentText from "../AssetAccordionHeaderEndContentText";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { isAssetProcessing, processingRefetchInterval } from "../../commons/asset-processing";
import { useMediaQuery } from "react-responsive";
import MainLoader from "../../layout/MainLoader";
import { Tooltip } from "primereact/tooltip";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import Show from "../Show";
import toCurrency from "../../utils/currency";
import AssetSourceIndicator from "../AssetSourceIndicator";
import { formatTenure, formatTimeToDue, getAssetDetailsHeading, getAssetListSubtitle } from "../../commons/asset-details-heading";
import { formatDebentureYieldShort } from "../../commons/debenture-yield";

interface IAssetAccordionHeaderEndContentProps {
    walletPercentage: number;
    totalPosition: number;
    result: number;
    rentability: number;
}

const DetailStat = ({
    label,
    value,
    currency,
    percent
}: {
    label: string;
    value: number;
    currency?: boolean;
    percent?: boolean;
}) => (
    <Container flexColumn gap={1}>
        <Label>{label}</Label>
        {percent ? (
            <PercentageText color="900" size="lg" fontWeight="semibold">
                {value}
            </PercentageText>
        ) : currency ? (
            <Currency value={value}>
                {(formatted) => (
                    <Text color="900" size="lg" fontWeight="semibold">{formatted}</Text>
                )}
            </Currency>
        ) : (
            <Text color="900" size="lg" fontWeight="semibold">{value}</Text>
        )}
    </Container>
);

const AssetDetailsSummary = ({
    title,
    subtitle,
    currentAmount,
    investedValue,
    walletTotal
}: {
    title: string;
    subtitle?: string;
    currentAmount: number;
    investedValue: number;
    walletTotal?: number;
}) => {
    const profit = currentAmount - investedValue;
    const profitPct = investedValue ? (profit * 100) / investedValue : 0;
    const walletPct = walletTotal ? (currentAmount * 100) / walletTotal : null;

    return (
        <Container flexColumn gap={3}>
            <Container flexColumn gap={1}>
                <Text color="900" size="xl" fontWeight="semibold">{title}</Text>
                {!!subtitle && (
                    <Text color="600" size="sm">{subtitle}</Text>
                )}
            </Container>
            <Container wrap gap={4}>
                <DetailStat label="Valor atual" value={currentAmount} currency />
                <DetailStat label="Valor investido" value={investedValue} currency />
                <DetailStat label="Resultado" value={profit} currency />
                <DetailStat label="Rentabilidade" value={profitPct} percent />
                {walletPct != null && (
                    <DetailStat label="% da carteira" value={walletPct} percent />
                )}
            </Container>
        </Container>
    );
};

const AssetAccordionHeaderEndContent = ({ walletPercentage, totalPosition, result, rentability }: IAssetAccordionHeaderEndContentProps) => {
    return (
        <Container wrapResponsive gap={3}>
            <AssetAccordionHeaderEndContentText
                title="% da carteira"
                value={walletPercentage}
                isPercentage
            />

            <AssetAccordionHeaderEndContentText
                title="Posição atual"
                value={totalPosition}
                isCurrency
            />

            <AssetAccordionHeaderEndContentText
                title="Resultado"
                value={result}
                isCurrency
            />

            <AssetAccordionHeaderEndContentText
                title="Percentual da posição"
                value={rentability}
                isPercentage
            />
        </Container>
    )
}

interface IAssetDetailsProps {
    id: string;
}

const AssetDetails = ({ id }: IAssetDetailsProps) => {
    const { toPDF, targetRef } = usePDF({ filename: 'ativo.pdf' });
    const toast = useRef<Toast>(null);
    const homeContext = useHomeContext();
    const [selectedIndexName, setSelectedIndexName] = useState<string>("CDI");
    const authHttp = useAuthHttp();
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", id],
        gcTime: 0,
        refetchInterval: (query) => processingRefetchInterval(
            isAssetProcessing(query.state.data?.asset_info?.asset_status)
        ),
        queryFn: async () => {
            if (!id) {
                return {
                    asset_info: null
                };
            }

            return await authHttp.doGet<IAssetDetailsResponse>(`asset/${id}`)
        }
    });

    const documentStyle = getComputedStyle(document.documentElement);
    const shortMonths = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

    const profitsByEvolution = assetDetailsQuery.data?.asset_info?.asset_profit_evolution?.reduce((acc, curr) => {
        const month = moment(curr.reference_date).format("YYYY-MM");
        if (!acc[month] || moment(curr.reference_date).isAfter(acc[month].reference_date)) {
            acc[month] = curr;
        }
        return acc;
    }, {} as Record<string, IAssetProfitEvolution>) ?? {};

    const awaitingAsOf = assetDetailsQuery.data?.asset_info?.awaiting_index?.as_of?.replace("-", "");
    const profitsBenchmark = (assetDetailsQuery.data?.asset_info?.asset_profit_benchmark ?? [])
        .filter(p => p.index_name === "PROFIT")
        .filter(p => !awaitingAsOf || p.month_reference <= awaitingAsOf)
        .filter(p => moment(p.month_reference, "YYYYMM").year() === selectedYear);

    const assetReference = useCallback(() => {
        if (!assetDetailsQuery.data?.asset_info) return "";

        switch (assetDetailsQuery.data.asset_info.asset_type.toLowerCase()) {
            case AssetMainType.PrivateBound:
                return AssetFactory.getIndexer(assetDetailsQuery.data as IAssetDetailsResponse)
                    ?? "";
            case AssetMainType.TreasureBound:
                return assetDetailsQuery.data.asset_info.asset_tb_details.treasure.TrsrBd.FinIndxs?.nm
                    ?? assetDetailsQuery.data.asset_info.asset_tb_details.treasure.TrsrBdType?.nm
                    ?? "";
            case AssetMainType.Debenture:
                return assetDetailsQuery.data.asset_info.asset_db_details.debenture.deb_index;
            case AssetMainType.Currency:
                return assetDetailsQuery.data.asset_info.asset_cy_details.currency_symbol.name;
            case AssetMainType.InvestimentFunds:
                return assetDetailsQuery.data.asset_info.asset_if_details.if_ticker_symbol;
            case AssetMainType.Stock:
                return assetDetailsQuery.data.asset_info.asset_st_details.st_ticker_symbol;
            case AssetMainType.SavingAccount:
                return "Poupança";
            case AssetMainType.CheckingAccount:
                return "Conta Corrente";
            case AssetMainType.Customized:
                return "Personalizado";
            case AssetMainType.Property:
                return "Imóvel";
            case AssetMainType.Vehicle:
                return "Veículo";
            case AssetMainType.Cripto:
                return assetDetailsQuery.data.asset_info.asset_cm_details.cripto_symbol.currency_base;
        }
    }, [assetDetailsQuery.data]);
  
    const chartData = {
        labels: profitsBenchmark.map(profit => shortMonths[moment(profit.month_reference, "YYYYMM").month()]),
        datasets: [
            {
                label: assetReference(),
                data: profitsBenchmark.filter(p => p.index_name == "PROFIT").length
                    ? profitsBenchmark.filter(p => p.index_name == "PROFIT").map(b => b.month_percent_value)
                    : profitsBenchmark.map(profit => {
                        return profitsByEvolution[moment(profit.month_reference, "YYYYMM").format("YYYY-MM")]?.reference_ratio ?? 0;
                    }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--primary-500'),
                tension: 0.4
            },
            {
                label: selectedIndexName,
                data: profitsBenchmark.map(profit => {
                    const benchmark = homeContext.benchmark.find(b =>
                        b.index_name.toLowerCase().startsWith(selectedIndexName.toLowerCase())
                        && moment(b.month_reference, "YYYYMM").isSame(moment(profit.month_reference, "YYYYMM")));
                    return benchmark ? benchmark.month_percent_value : 0;
                }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--gray-900'),
                tension: 0.4
            }
        ]
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
    };

    const interests = assetDetailsQuery.data?.asset_info?.asset_event_asset?.map((event) => {
        return {
            month_reference: event.event_date,
            event_date: event.event_date,
            event_value: event.event_value,
            event_fee: event.event_fee,
            event_type: event.event_type
        };
    });

    const handleRemoveEventUser = async (eventId: string) => {
        confirmDialog({
            message: "Tem certeza que deseja remover este evento?",
            header: "Remover evento",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: async () => {
                await removeEventUserMutation.mutateAsync(eventId);
            },
        });
    }

    const removeEventUserMutation = useMutation({
        mutationFn: async (eventId: string) => {
            return await authHttp.doDelete(`asset/${id}/event/${eventId}`);
        },
        onSuccess: () => {
            assetDetailsQuery.refetch();
            homeContext.refresh();
            toast.current?.show({ severity: "success", summary: "Sucesso", detail: "Evento removido com sucesso", life: 3000 });
        },
        onError: () => {
            toast.current?.show({ severity: "error", summary: "Erro", detail: "Erro ao remover evento", life: 3000 });
        }
    });

    return (
        <>
            <Toast ref={toast} />

            {
                assetDetailsQuery.isLoading && (
                    <Container center fullWidth>
                        <MainLoader fullHeight={false} minHeight="300px" />
                    </Container>
                )
            }

            {
                !assetDetailsQuery.isLoading && (
                    <>
                        {
                            isAssetProcessing(assetDetailsQuery.data?.asset_info?.asset_status) && (
                                <Message
                                    severity="warn"
                                    className="w-full mb-3"
                                    text="Este ativo está sendo atualizado. Os valores podem mudar em instantes — não é necessário repetir a operação."
                                />
                            )
                        }
                        {
                            !!assetDetailsQuery.data?.asset_info?.awaiting_index?.message && (
                                <Message
                                    severity="info"
                                    className="w-full mb-3"
                                    text={assetDetailsQuery.data.asset_info.awaiting_index.message}
                                />
                            )
                        }
                        <Container marginVertical="2">
                            <Button onClick={() => toPDF({ page: { margin: Margin.MEDIUM } })}>
                                Exportar para PDF
                            </Button>
                        </Container>

                        <div ref={targetRef}>
                            <Container flexColumn gap={3}>
                                {(() => {
                                    const info = assetDetailsQuery.data?.asset_info;
                                    const heading = getAssetDetailsHeading(info);
                                    return (
                                        <AssetDetailsSummary
                                            title={heading.title}
                                            subtitle={heading.subtitle}
                                            currentAmount={info?.asset_current_value ?? 0}
                                            investedValue={residualInvested(info?.asset_event_user)}
                                            walletTotal={homeContext.wallet?.totalValue}
                                        />
                                    );
                                })()}
                                <Container flexColumn className="align-items-end" gap={2}>
                                    <Label>Comparar com</Label>
                                    <Dropdown
                                        value={selectedIndexName}
                                        style={{ width: '10rem' }}
                                        options={[...new Set(homeContext.benchmark.filter(b => b.index_name.toLowerCase() !== "profit").map(b => b.index_name).map(label => label.split("-")[0]))]}
                                        onChange={e => setSelectedIndexName(e.value)}
                                    />
                                </Container>

                                <Container className="justify-content-end align-items-center" gap={2}>
                                    <Button
                                        icon="pi pi-arrow-left"
                                        outlined
                                        rounded
                                        className="border-none"
                                        onClick={() => {
                                            setSelectedYear(selectedYear => selectedYear - 1);
                                        }}
                                    />
            
                                    <Text color="primary" className="text-2xl font-bold">
                                        {selectedYear}
                                    </Text>
            
                                    <Button
                                        icon="pi pi-arrow-right"
                                        outlined
                                        rounded
                                        className="border-none"
                                        onClick={() => {
                                            setSelectedYear(selectedYear => selectedYear + 1);
                                        }}
                                    />
                                </Container>

                                <Chart
                                    type="line"
                                    data={chartData}
                                    options={chartOptions}
                                    style={{ width: '100%' }}
                                />

                                <Container className="p-accordion-fixed-income" flexColumn>
                                    <Accordion activeIndex={[0, 1, 2, 3]} multiple>
                                        <AccordionTab disabled header="Detalhes">
                                            <Container gap={3} wrap>
                                                <Container flexColumn gap={2}>
                                                    <Label>Origem do cadastro</Label>
                                                    <AssetSourceIndicator
                                                        showLabel
                                                        sourceRaw={assetDetailsQuery.data?.asset_info?.asset_source}
                                                    />
                                                </Container>

                                                <Show when={!!(
                                                    assetDetailsQuery.data?.asset_info?.asset_type?.toLowerCase() === AssetMainType.PrivateBound
                                                        ? assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_type
                                                        : assetDetailsQuery.data?.asset_info?.asset_sub_type
                                                )}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Tipo</Label>
                                                        <Text>
                                                            {assetDetailsQuery.data?.asset_info?.asset_type?.toLowerCase() === AssetMainType.PrivateBound
                                                                ? assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_type
                                                                : assetDetailsQuery.data?.asset_info?.asset_sub_type}
                                                        </Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!AssetFactory.getIndexer(assetDetailsQuery.data as IAssetDetailsResponse)}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Indexador</Label>
                                                        <Text>{AssetFactory.getIndexer(assetDetailsQuery.data as IAssetDetailsResponse)}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={
                                                    assetDetailsQuery.data?.asset_info?.asset_type?.toLowerCase() !== AssetMainType.PrivateBound
                                                    && !!AssetFactory.getTax(assetDetailsQuery.data as IAssetDetailsResponse)
                                                }>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Taxa</Label>
                                                        <Text>{AssetFactory.getTax(assetDetailsQuery.data as IAssetDetailsResponse)}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={
                                                    assetDetailsQuery.data?.asset_info?.asset_type?.toLowerCase() === AssetMainType.CheckingAccount
                                                    && assetDetailsQuery.data?.asset_info?.asset_ca_details?.ca_rate_cdi != null
                                                }>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Indexador</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_ca_details?.ca_rate_cdi}% do CDI</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_cy_details?.currency_symbol?.code}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Código da moeda</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_cy_details?.currency_symbol?.code}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_cm_details?.cripto_symbol?.code}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Código da cripto moeda</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_cm_details?.cripto_symbol?.code}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_if_details?.if_ticker_symbol}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Fundo de investimento</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_if_details?.if_ticker_symbol}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_st_details?.st_ticker_symbol}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Ação</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_st_details?.st_ticker_symbol}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_db_details?.db_code}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Debênture</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_db_details?.db_code}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!formatDebentureYieldShort(
                                                    assetDetailsQuery.data?.asset_info?.asset_db_details?.debenture?.deb_index,
                                                    assetDetailsQuery.data?.asset_info?.asset_db_details?.debenture?.deb_interest_rate
                                                )}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Como rende</Label>
                                                        <Text>
                                                            {formatDebentureYieldShort(
                                                                assetDetailsQuery.data?.asset_info?.asset_db_details?.debenture?.deb_index,
                                                                assetDetailsQuery.data?.asset_info?.asset_db_details?.debenture?.deb_interest_rate
                                                            )}
                                                        </Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_tb_details?.treasure?.TrsrBd?.FinIndxs?.nm}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Indexador</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_tb_details?.treasure?.TrsrBd?.FinIndxs?.nm}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={assetDetailsQuery.data?.asset_info?.asset_tb_details?.tb_rate_pre_plus != null}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Taxa na compra</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_tb_details?.tb_rate_pre_plus}% a.a.</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={
                                                    (assetDetailsQuery.data?.asset_info?.asset_st_details?.st_quantity_actual_amount
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_st_details?.st_qtd_prch
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_quantity_actual_amount
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_qtd_prch
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_quantity_actual_amount
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_quantity_prch
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_quantity_actual_amount
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_quantity_prch
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_tb_details?.tb_quantity_prch
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_db_details?.db_quantity_prch) != null
                                                }>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Quantidade</Label>
                                                        <Text>
                                                            {assetDetailsQuery.data?.asset_info?.asset_st_details?.st_quantity_actual_amount
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_st_details?.st_qtd_prch
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_quantity_actual_amount
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_qtd_prch
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_quantity_actual_amount
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_quantity_prch
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_quantity_actual_amount
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_quantity_prch
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_tb_details?.tb_quantity_prch
                                                                ?? assetDetailsQuery.data?.asset_info?.asset_db_details?.db_quantity_prch}
                                                        </Text>
                                                    </Container>
                                                </Show>

                                                <Show when={
                                                    (assetDetailsQuery.data?.asset_info?.asset_st_details?.st_avg_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_avg_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_avg_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_avg_price) != null
                                                }>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Preço médio</Label>
                                                        <Currency value={
                                                            assetDetailsQuery.data?.asset_info?.asset_st_details?.st_avg_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_avg_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_avg_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_avg_price
                                                            ?? 0
                                                        }>
                                                            {(value) => <Text>{value}</Text>}
                                                        </Currency>
                                                    </Container>
                                                </Show>

                                                <Show when={
                                                    (assetDetailsQuery.data?.asset_info?.asset_st_details?.st_current_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_current_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_current_price
                                                        ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_current_price) != null
                                                }>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Preço atual</Label>
                                                        <Currency value={
                                                            assetDetailsQuery.data?.asset_info?.asset_st_details?.st_current_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_if_details?.if_current_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_cy_details?.cy_current_price
                                                            ?? assetDetailsQuery.data?.asset_info?.asset_cm_details?.cm_current_price
                                                            ?? 0
                                                        }>
                                                            {(value) => <Text>{value}</Text>}
                                                        </Currency>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_fin_issue?.inst_name}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Banco emissor</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_fin_issue?.inst_name}</Text>
                                                    </Container>
                                                </Show>
                                                
                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_fin_negotiating?.inst_name}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Onde comprou</Label>
                                                        <Text>{assetDetailsQuery.data?.asset_info?.asset_fin_negotiating?.inst_name}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_purchase_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da compra</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.asset_info?.asset_purchase_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_due_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data do vencimento</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.asset_info?.asset_due_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!formatTenure(assetDetailsQuery.data?.asset_info?.asset_purchase_date)}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Tempo de posse</Label>
                                                        <Text>{formatTenure(assetDetailsQuery.data?.asset_info?.asset_purchase_date)}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!formatTimeToDue(assetDetailsQuery.data?.asset_info?.asset_due_date)}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Prazo restante</Label>
                                                        <Text>{formatTimeToDue(assetDetailsQuery.data?.asset_info?.asset_due_date)}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_oc_brokerage}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Corretagem</Label>
                                                        <Currency value={assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_oc_brokerage}>
                                                            {(value) => <Text>{value}</Text>}
                                                        </Currency>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_oc_others}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Outros custos</Label>
                                                        <Currency value={assetDetailsQuery.data?.asset_info?.asset_pb_details?.pb_oc_others}>
                                                            {(value) => <Text>{value}</Text>}
                                                        </Currency>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_create_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data do cadastro</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.asset_info?.asset_create_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.asset_info?.asset_last_consolidate_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da última atualização</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.asset_info?.asset_last_consolidate_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>
                                            </Container>
                                        </AccordionTab>

                                        <AccordionTab disabled header="Rendimento Mensal">
                                            <DataTable
                                                value={
                                                    (assetDetailsQuery.data?.asset_info?.asset_profit_benchmark?.filter(p => p.index_name == "PROFIT").length
                                                        ? assetDetailsQuery.data?.asset_info?.asset_profit_benchmark.filter(p => p.index_name == "PROFIT" && (!awaitingAsOf || p.month_reference <= awaitingAsOf))
                                                        : Object.values(profitsByEvolution)).sort((a: any, b: any) => {
                                                            return moment(a.reference_date ?? a.month_reference).isAfter(moment(b.reference_date ?? b.month_reference)) ? -1 : 1;
                                                        })
                                                }
                                                tableStyle={{ minWidth: '100%' }}
                                            >
                                                <Column
                                                    header="Mês/Ano"
                                                    body={row => {
                                                        const month = moment(row.month_reference ?? row.reference_date).toDate();

                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {months[month.getMonth()] + " " + month.getFullYear()}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Saldo"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={((row.month_initial_value ?? row.asset_balance ?? 0) + (row.month_evolution_value ?? row.month_profit_value ?? 0))}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Resultado"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={((row.month_profit_value ?? row.period_profit))}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Rentabilidade"
                                                    body={row => {
                                                        return (
                                                            <PercentageText>
                                                                {row.month_percent_value ?? row.reference_ratio}
                                                            </PercentageText>
                                                        );
                                                    }}
                                                />
                                            </DataTable>
                                        </AccordionTab>

                                        <AccordionTab disabled header="Pagamento de Proventos">
                                            <DataTable
                                                value={
                                                    interests?.sort((a, b) => {
                                                        return moment(a.month_reference).isAfter(moment(b.month_reference)) ? -1 : 1;
                                                    }) ?? []
                                                }
                                                tableStyle={{ minWidth: '100%' }}
                                                emptyMessage="Nenhum pagamento de juros"
                                            >
                                                <Column
                                                    header="Mês/Ano"
                                                    body={row => {
                                                        const month = moment(row.month_reference).toDate();

                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {months[month.getMonth()] + " " + month.getFullYear()}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Data do Provento"
                                                    body={row => {
                                                        const eventDate = moment(row.event_date).format("DD/MM/YYYY");

                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {eventDate}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Valor Total"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={(row.event_value ?? 0)}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Tipo"
                                                    body={row => {
                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {row.event_type}
                                                            </Text>
                                                        )
                                                    }}
                                                />

                                                <Column
                                                    header="Taxa"
                                                    body={row => {
                                                        return <>
                                                            {
                                                                row.event_fee ? (
                                                                    <PercentageText>
                                                                        {row.event_fee}
                                                                    </PercentageText>
                                                                ) : (
                                                                    <Text
                                                                        color="900"
                                                                        size="base"
                                                                        fontWeight="normal"
                                                                    >
                                                                        -
                                                                    </Text>
                                                                )
                                                            }
                                                        </>;
                                                    }}
                                                />
                                            </DataTable>
                                        </AccordionTab>

                                        <AccordionTab disabled header="Movimentações">
                                            <DataTable
                                                value={
                                                    assetDetailsQuery.data?.asset_info?.asset_event_user.sort((a, b) => {
                                                        return moment(a.event_date).isAfter(moment(b.event_date)) ? -1 : 1;
                                                    })
                                                }
                                                tableStyle={{ minWidth: '100%' }}
                                                emptyMessage="Nenhuma movimentação"
                                            >
                                                <Column
                                                    header="Data"
                                                    body={row => {
                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {moment(row.event_date.split("T")[0]).format("DD/MM/YYYY")}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                {
                                                    assetDetailsQuery.data?.asset_info?.asset_event_user.some(e => !!e.event_quantity) && (
                                                        <Column
                                                            header="Quantidade"
                                                            body={row => {
                                                                return (
                                                                    <Text
                                                                        color="900"
                                                                        size="base"
                                                                        fontWeight="normal"
                                                                    >
                                                                        {!row.event_quantity ? "-" : row.event_quantity}
                                                                    </Text>
                                                                );
                                                            }}
                                                        />
                                                    )
                                                }

                                                <Column
                                                    header="Tipo"
                                                    body={row => {
                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {
                                                                    row.event_type === "CHARGE"
                                                                        ? "Aporte"
                                                                        : "Saque"
                                                                }
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Valor"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={(row.event_value ?? 0)}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Ações"
                                                    frozen
                                                    alignFrozen="right"
                                                    body={row => {
                                                        const isFirstEventRegistered = assetDetailsQuery.data?.asset_info?.asset_event_user.findIndex(e => e.id == row.id) == (assetDetailsQuery.data?.asset_info?.asset_event_user.length ?? 0) - 1;

                                                        return (
                                                            <Button
                                                                type="button"
                                                                icon="pi pi-trash"
                                                                className={`p-button-sm p-button-text text-red-500`}
                                                                loading={(removeEventUserMutation.isPending && removeEventUserMutation.variables == row.id)}
                                                                disabled={(removeEventUserMutation.isPending && removeEventUserMutation.variables == row.id) || isFirstEventRegistered}
                                                                onClick={async () => {
                                                                    await handleRemoveEventUser(row.id);
                                                                }}
                                                            />
                                                        )
                                                    }}
                                                />
                                            </DataTable>
                                        </AccordionTab>
                                    </Accordion>
                                </Container>
                            </Container>
                        </div>
                    </>
                )
            }
        </>
    );
}

const VehicleDetails = ({ id }: IAssetDetailsProps) => {
    const { toPDF, targetRef } = usePDF({ filename: 'veículo.pdf' });
    const homeContext = useHomeContext();
    const [selectedIndexName, setSelectedIndexName] = useState<string>("CDI");
    const authHttp = useAuthHttp();
    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", id],
        gcTime: 0,
        queryFn: async () => {
            if (!id) {
                return {
                    vehicle_user: null
                };
            }

            return await authHttp.doGet<IVehicleResult>(`vehicle/user/${id}`);
        }
    });

    const documentStyle = getComputedStyle(document.documentElement);
    const shortMonths = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const currentDate = moment().add(1, "month").toDate();
    const last12Months: Date[] = [];

    for (let i = 6; i >= 0; i--) {
        const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        if (currentDate.getMonth() == month.getMonth()) continue;
        last12Months.push(month);
    }

    const profitsByEvolution = assetDetailsQuery.data?.vehicle_user?.vehicle_valuation_evolution?.reduce((acc, curr) => {
        const month = moment(curr.valuation_month_reference).format("YYYY-MM");
        if (!acc[month] || moment(curr.valuation_month_reference).isAfter(acc[month].reference_date)) {
            acc[month] = {
                reference_date: curr.valuation_month_reference,
                asset_balance: curr.valuation_value,
                reference_ratio: (((curr.valuation_value * 100) / (assetDetailsQuery.data.vehicle_user?.vehicle_purchase_value ?? 1) - 100)),
                period_profit: 0,
                previous_event_asset_balance: 0,
            };
        }
        return acc;
    }, {} as Record<string, IAssetProfitEvolution>) ?? {};

    const assetReference = useCallback(() => {
        if (!assetDetailsQuery.data?.vehicle_user) return "";

        return assetDetailsQuery.data.vehicle_user?.vehicle_model.vehicle_model_label;
    }, [assetDetailsQuery.data]);

    const chartData = {
        labels: last12Months.map(month => shortMonths[month.getMonth()]),
        datasets: [
            {
                label: assetReference(),
                data: last12Months.map(month => {
                    return profitsByEvolution[moment(new Date(month.getFullYear(), month.getMonth(), 1)).format("YYYY-MM")]?.reference_ratio ?? 0;
                }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--primary-500'),
                tension: 0.4
            },
            {
                label: selectedIndexName,
                data: last12Months.map(month => {
                    const benchmark = homeContext.benchmark.find(b =>
                        b.index_name.toLowerCase().startsWith(selectedIndexName.toLowerCase())
                        && moment(b.month_reference, "YYYYMM").isSame(moment(new Date(month.getFullYear(), month.getMonth(), 1))));
                    return benchmark ? benchmark.month_percent_value : 0;
                }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--gray-900'),
                tension: 0.4
            }
        ]
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
    };

    return (
        <>
            {
                assetDetailsQuery.isLoading && (
                    <Container center fullWidth>
                        <MainLoader fullHeight={false} minHeight="300px" />
                    </Container>
                )
            }

            {
                !assetDetailsQuery.isLoading && (
                    <>
                        <Container marginVertical="2">
                            <Button onClick={() => toPDF({ page: { margin: Margin.MEDIUM } })}>
                                Exportar para PDF
                            </Button>
                        </Container>

                        <div ref={targetRef}>
                            <Container flexColumn gap={3}>
                                <AssetDetailsSummary
                                    title={
                                        assetDetailsQuery.data?.vehicle_user?.vehicle_alias
                                        || assetDetailsQuery.data?.vehicle_user?.vehicle_model?.vehicle_model_label
                                        || "Veículo"
                                    }
                                    subtitle={[
                                        assetDetailsQuery.data?.vehicle_user?.vehicle_model?.brand?.vehicle_model_brand_label,
                                        assetDetailsQuery.data?.vehicle_user?.vehicle_type_desc
                                    ].filter(Boolean).join(" · ")}
                                    currentAmount={assetDetailsQuery.data?.vehicle_user?.vehicle_valuation_value ?? 0}
                                    investedValue={assetDetailsQuery.data?.vehicle_user?.vehicle_purchase_value ?? 0}
                                    walletTotal={homeContext.wallet?.totalValue}
                                />
                                <Container flexColumn className="align-items-end" gap={2}>
                                    <Label>Comparar com</Label>
                                    <Dropdown
                                        value={selectedIndexName}
                                        style={{ width: '10rem' }}
                                        options={[...new Set(homeContext.benchmark.filter(b => b.index_name.toLowerCase() !== "profit").map(b => b.index_name).map(label => label.split("-")[0]))]}
                                        onChange={e => setSelectedIndexName(e.value)}
                                    />
                                </Container>

                                <Chart
                                    type="line"
                                    data={chartData}
                                    options={chartOptions}
                                    style={{ width: '100%' }}
                                />

                                <Container className="p-accordion-vehicles" flexColumn>
                                    <Accordion activeIndex={[0, 1]} multiple>
                                        <AccordionTab disabled header="Detalhes">
                                            <Container gap={3} wrap>
                                                <Container flexColumn gap={2}>
                                                    <Label>Origem do cadastro</Label>
                                                    <AssetSourceIndicator
                                                        showLabel
                                                        sourceRaw={assetDetailsQuery.data?.vehicle_user?.vehicle_source}
                                                    />
                                                </Container>

                                                <Show when={!!assetDetailsQuery.data?.vehicle_user?.vehicle_model.brand?.vehicle_model_brand_label}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Marca</Label>
                                                        <Text>{assetDetailsQuery.data?.vehicle_user?.vehicle_model.brand?.vehicle_model_brand_label}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.vehicle_user?.vehicle_model?.vehicle_model_label}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Modelo</Label>
                                                        <Text>{assetDetailsQuery.data?.vehicle_user?.vehicle_model?.vehicle_model_label}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.vehicle_user?.vehicle_year}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Ano</Label>
                                                        <Text>{assetDetailsQuery.data?.vehicle_user?.vehicle_year}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.vehicle_user?.vehicle_purchase_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da compra</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.vehicle_user?.vehicle_purchase_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.vehicle_user?.vehicle_last_consolidate_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da última atualização</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.vehicle_user?.vehicle_last_consolidate_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>
                                            </Container>
                                        </AccordionTab>

                                        <AccordionTab disabled header="Rendimento Mensal">
                                            <DataTable
                                                value={
                                                    assetDetailsQuery.data?.vehicle_user?.vehicle_valuation_evolution.sort((a, b) => {
                                                        return moment(a.valuation_month_reference).isAfter(moment(b.valuation_month_reference)) ? -1 : 1;
                                                    })
                                                }
                                                tableStyle={{ minWidth: '100%' }}
                                            >
                                                <Column
                                                    header="Mês/Ano"
                                                    body={row => {
                                                        const month = moment(row.valuation_month_reference).toDate();

                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {months[month.getMonth()] + " " + month.getFullYear()}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Valor atual"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={((row.valuation_value))}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Rentabilidade"
                                                    body={row => {
                                                        return (
                                                            <PercentageText>
                                                                {(((row.valuation_value * 100) / (assetDetailsQuery.data?.vehicle_user?.vehicle_purchase_value ?? 1)) - 100)}
                                                            </PercentageText>
                                                        );
                                                    }}
                                                />
                                            </DataTable>
                                        </AccordionTab>
                                    </Accordion>
                                </Container>
                            </Container>
                        </div>
                    </>
                )
            }
        </>
    );
}

const PropertyDetails = ({ id }: IAssetDetailsProps) => {
    const { toPDF, targetRef } = usePDF({ filename: 'imóvel.pdf' });
    const homeContext = useHomeContext();
    const [selectedIndexName, setSelectedIndexName] = useState<string>("CDI");
    const authHttp = useAuthHttp();
    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", id],
        gcTime: 0,
        queryFn: async () => {
            if (!id) {
                return {
                    property_user: null
                };
            }

            return await authHttp.doGet<IPropertyDetailsResult>(`property/user/${id}`);
        }
    });

    const documentStyle = getComputedStyle(document.documentElement);
    const shortMonths = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const currentDate = moment().add(1, "month").toDate();
    const last12Months: Date[] = [];

    for (let i = 6; i >= 0; i--) {
        const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        if (currentDate.getMonth() == month.getMonth()) continue;
        last12Months.push(month);
    }

    const profitsByEvolution = assetDetailsQuery.data?.property_user?.property_profit_evolution?.reduce((acc, curr) => {
        const month = moment(curr.reference_date).format("YYYY-MM");
        if (!acc[month] || moment(curr.reference_date).isAfter(acc[month].reference_date)) {
            acc[month] = {
                reference_date: curr.reference_date,
                asset_balance: curr.property_valuation,
                reference_ratio: ((((curr.property_valuation * 100) / (assetDetailsQuery.data.property_user?.property_purchase_value ?? 1)) - 100)),
                period_profit: 0,
                previous_event_asset_balance: 0,
            };
        }
        return acc;
    }, {} as Record<string, IAssetProfitEvolution>) ?? {};

    const assetReference = useCallback(() => {
        if (!assetDetailsQuery.data?.property_user) return "";

        return assetDetailsQuery.data.property_user?.property_alias ?? "Imóvel";
    }, [assetDetailsQuery.data]);

    const chartData = {
        labels: last12Months.map(month => shortMonths[month.getMonth()]),
        datasets: [
            {
                label: assetReference(),
                data: last12Months.map(month => {
                    return profitsByEvolution[moment(new Date(month.getFullYear(), month.getMonth(), 1)).format("YYYY-MM")]?.reference_ratio ?? 0;
                }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--primary-500'),
                tension: 0.4
            },
            {
                label: selectedIndexName,
                data: last12Months.map(month => {
                    const benchmark = homeContext.benchmark.find(b =>
                        b.index_name.toLowerCase().startsWith(selectedIndexName.toLowerCase())
                        && moment(b.month_reference, "YYYYMM").isSame(moment(new Date(month.getFullYear(), month.getMonth(), 1))));
                    return benchmark ? benchmark.month_percent_value : 0;
                }),
                fill: false,
                borderColor: documentStyle.getPropertyValue('--gray-900'),
                tension: 0.4
            }
        ]
    };

    const chartOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
    };

    return (
        <>
            {
                assetDetailsQuery.isLoading && (
                    <Container center fullWidth>
                        <MainLoader fullHeight={false} minHeight="300px" />
                    </Container>
                )
            }

            {
                !assetDetailsQuery.isLoading && (
                    <>
                        <Message severity="warn" className="mb-2" text="Atenção: Para imóveis, não exibimos dados históricos. As informações são contabilizadas apenas a partir do mês de cadastro." />

                        <Container marginVertical="2">
                            <Button onClick={() => toPDF({ page: { margin: Margin.MEDIUM } })}>
                                Exportar para PDF
                            </Button>
                        </Container>

                        <div ref={targetRef}>
                            <Container flexColumn gap={3}>
                                <AssetDetailsSummary
                                    title={assetDetailsQuery.data?.property_user?.property_alias || "Imóvel"}
                                    subtitle={[
                                        assetDetailsQuery.data?.property_user?.property_type_desc,
                                        assetDetailsQuery.data?.property_user?.property_city
                                    ].filter(Boolean).join(" · ")}
                                    currentAmount={
                                        assetDetailsQuery.data?.property_user?.property_actual_value
                                        ?? assetDetailsQuery.data?.property_user?.property_valuation_value
                                        ?? 0
                                    }
                                    investedValue={assetDetailsQuery.data?.property_user?.property_purchase_value ?? 0}
                                    walletTotal={homeContext.wallet?.totalValue}
                                />
                                <Container flexColumn className="align-items-end" gap={2}>
                                    <Label>Comparar com</Label>
                                    <Dropdown
                                        value={selectedIndexName}
                                        style={{ width: '10rem' }}
                                        options={[...new Set(homeContext.benchmark.filter(b => b.index_name.toLowerCase() !== "profit").map(b => b.index_name).map(label => label.split("-")[0]))]}
                                        onChange={e => setSelectedIndexName(e.value)}
                                    />
                                </Container>

                                <Chart
                                    type="line"
                                    data={chartData}
                                    options={chartOptions}
                                    style={{ width: '100%' }}
                                />

                                <Container className="p-accordion-properties" flexColumn>
                                    <Accordion activeIndex={[0, 1]} multiple>
                                        <AccordionTab disabled header="Detalhes">
                                            <Container gap={3} wrap>
                                                <Container flexColumn gap={2}>
                                                    <Label>Origem do cadastro</Label>
                                                    <AssetSourceIndicator
                                                        showLabel
                                                        sourceRaw={assetDetailsQuery.data?.property_user?.property_source}
                                                    />
                                                </Container>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_type_desc}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Tipo</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_type_desc}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_category}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Categoria</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_category == 1 ? "Residencial" : "Comercial"}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_state}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Estado</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_state}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_city}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Cidade</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_city}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_district}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Bairro</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_district}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_place}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Localização</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_place}</Text>
                                                    </Container>
                                                </Show>
                                                
                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_construction_year}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Ano da construção</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_construction_year}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_area}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Área</Label>
                                                        <Text>{assetDetailsQuery.data?.property_user?.property_area}m2</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_purchase_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da compra</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.property_user?.property_purchase_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>

                                                <Show when={!!assetDetailsQuery.data?.property_user?.property_last_valuation_date}>
                                                    <Container flexColumn gap={2}>
                                                        <Label>Data da última atualização</Label>
                                                        <Text>{moment(assetDetailsQuery.data?.property_user?.property_last_valuation_date).format("DD/MM/YYYY")}</Text>
                                                    </Container>
                                                </Show>
                                            </Container>
                                        </AccordionTab>

                                        <AccordionTab disabled header="Rendimento Mensal">
                                            <DataTable
                                                value={
                                                    assetDetailsQuery.data?.property_user?.property_profit_evolution.sort((a, b) => {
                                                        return moment(a.reference_date).isAfter(moment(b.reference_date)) ? -1 : 1;
                                                    })
                                                }
                                                tableStyle={{ minWidth: '100%' }}
                                            >
                                                <Column
                                                    header="Mês/Ano"
                                                    body={row => {
                                                        const month = moment(row.reference_date).toDate();

                                                        return (
                                                            <Text
                                                                color="900"
                                                                size="base"
                                                                fontWeight="normal"
                                                            >
                                                                {months[month.getMonth()] + " " + month.getFullYear()}
                                                            </Text>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Valor atual"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={((row.property_valuation))}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Valor do m2"
                                                    body={row => {
                                                        return (
                                                            <Currency
                                                                value={((row.property_central_value))}
                                                            >
                                                                {(value) => value}
                                                            </Currency>
                                                        );
                                                    }}
                                                />

                                                <Column
                                                    header="Rentabilidade"
                                                    body={row => {
                                                        return (
                                                            <PercentageText>
                                                                {(((row.property_valuation * 100) / (assetDetailsQuery.data?.property_user?.property_purchase_value ?? 1)) - 100)}
                                                            </PercentageText>
                                                        );
                                                    }}
                                                />
                                            </DataTable>
                                        </AccordionTab>
                                    </Accordion>
                                </Container>
                            </Container>
                        </div>
                    </>
                )
            }
        </>
    );
}

interface ISaleFormProps {
    id: string;
    type: AssetMainType;
    name: string;
    visible: boolean;
    toast: Toast|null;
    onHide: () => void;
    onRefreshTable: () => Promise<void>;
}

const SaleForm = ({ id, type, name, visible, toast, onHide, onRefreshTable }: ISaleFormProps) => {
    const authHttp = useAuthHttp();

    const handleHide = () => {
        saleFormik.resetForm();
        onHide();
    }

    const saleMutation = useMutation({
        mutationFn: async ({ id, date, amount }: { id: string; date: Date; amount: number; }) => {
            toast?.show({ severity: 'info', summary: 'Aguarde', detail: `Executando venda` });
            
            let route = `/property/user/${id}/sale`;
            if (type == AssetMainType.Vehicle) route = `/vehicle/user/${id}/sale`;

            await authHttp.doPost(
                route, 
                {
                    event_date: date.toISOString(),
                    event_value: amount
                }
            );
        },
        onSuccess: () => {
            toast?.show({ severity: 'success', summary: 'Sucesso', detail: `Venda concluída com sucesso` });
            handleHide();
            onRefreshTable().then();
        },
        onError: (error) => {
            toast?.show({ severity: 'error', summary: 'Erro', detail: error.message ?? `Erro ao concluir venda` });
        }
    });

    const saleFormik = useFormik({
        initialValues: {
            date: new Date(),
            amount: 0
        },
        validationSchema: Yup.object().shape({
            date: Yup.date()
                .required("Campo data da venda é obrigatório"),
            amount: Yup.number()
                .required("Campo valor da venda é obrigatório")
                .min(1, "Valor de venda é obrigatório")
        }),
        onSubmit: async (values) => {
            await saleMutation.mutateAsync({
                id,
                amount: values.amount,
                date: values.date
            });
        }
    });

    return (
        <Dialog visible={visible} onHide={handleHide} header="Venda">
            <Container flexColumn gap={3}>
                <FormField
                    id="saleDate"
                    label="Data da venda"
                    hasError={!!saleFormik.errors.date && !!saleFormik.touched.date}
                    error={saleFormik.errors.date as string}
                >
                    <Calendar
                        invalid={!!saleFormik.errors.date && !!saleFormik.touched.date}
                        value={saleFormik.values.date}
                        dateFormat="dd/mm/yy"
                        readOnlyInput
                        showIcon
                        onChange={(event) => {
                            saleFormik.setFieldValue("date", event.value);
                        }}
                        locale="pt"
                    />
                </FormField>

                <FormField
                    id="saleValue"
                    hasError={!!saleFormik.errors.amount && !!saleFormik.touched.amount}
                    error={saleFormik.errors.amount}
                    label="Valor da venda"
                >
                    <InputNumber
                        invalid={!!saleFormik.errors.amount && !!saleFormik.touched.amount}
                        onValueChange={(e) => {
                            saleFormik.setFieldValue("amount", e.value);
                        }}
                        value={saleFormik.values.amount}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                    />
                </FormField>

                <p>Deseja continuar com a venda de <strong>{name}</strong>?</p>

                <Container className="justify-content-end" gap={2}>
                    <Button outlined onClick={() => handleHide()}>Não</Button>
                    <Button 
                        onClick={() => saleFormik.handleSubmit()}
                        loading={saleMutation.isPending}
                        className="gap-2"
                        type="submit"
                    >
                        Sim
                    </Button>
                </Container>
            </Container>
        </Dialog>
    )
}

const AssetTable = ({ model, type, onRefreshTable }: { onRefreshTable: () => Promise<void>, type: AssetMainType, model: IFixedIncomeModel[] | ICashflowModel[] | IVehicleModel[] | IPropertyModel[] | IVariableIncomeModel[] }) => {
    const [privateBondsEdit, setPrivateBondsEdit] = useState<boolean>(false);
    const [savingAccountEdit, setSavingAccountEdit] = useState<boolean>(false);
    const [treasureBoundsEdit, setTreasureBoundEdit] = useState<boolean>(false);
    const [checkingAccountEdit, setCheckingAccountEdit] = useState<boolean>(false);
    const [vehicleEdit, setVehicleEdit] = useState<boolean>(false);
    const [propertyEdit, setPropertyEdit] = useState<boolean>(false);
    const [customizedEdit, setCustomizedEdit] = useState<boolean>(false);
    const [debentureEdit, setDebentureEdit] = useState<boolean>(false);
    const [currencyEdit, setCurrencyEdit] = useState<boolean>(false);
    const [criptoEdit, setCriptoEdit] = useState<boolean>(false);
    const [fundEdit, setFundEdit] = useState<boolean>(false);
    const [stockEdit, setStockEdit] = useState<boolean>(false);
    const [editId, setEditId] = useState<string>("");
    const toast = useRef<Toast | null>(null);
    const [detailsIsOpen, setDetailsIsOpen] = useState<boolean>(false);
    const [selectedAssets, setSelectedAssets] = useState<IFixedIncomeModel[] | ICashflowModel[] | IVehicleModel[] | IPropertyModel[] | IVariableIncomeModel[]>([]);
    const isTablet = useMediaQuery({ minWidth: 569, maxWidth: 768 });
    const isMobile = useMediaQuery({ maxWidth: 568 });

    const [saleId, setSaleId] = useState<string|null>(null);

    const authHttp = useAuthHttp();
    const homeContext = useHomeContext();
    const analyticsAssetType = assetMainTypeToAnalyticsType(type);
    const items = [
        {
            label: "Detalhes",
            icon: "pi pi-eye",
            color: "text-primary",
            command: (id: string) => {
                trackAssetDetailsViewed(analyticsAssetType);
                setEditId(id);
                setDetailsIsOpen(true);
            }
        },
        {
            label: 'Editar',
            icon: 'pi pi-file-edit',
            color: "text-green-600",
            command: (id: string) => {
                trackAssetEditStarted(analyticsAssetType);
                setEditId(id);
                switch (type) {
                    case AssetMainType.PrivateBound:
                        setPrivateBondsEdit(true);
                        break;
                    case AssetMainType.SavingAccount:
                        setSavingAccountEdit(true);
                        break;
                    case AssetMainType.TreasureBound:
                        setTreasureBoundEdit(true);
                        break;
                    case AssetMainType.CheckingAccount:
                        setCheckingAccountEdit(true);
                        break;
                    case AssetMainType.Customized:
                        setCustomizedEdit(true);
                        break;
                    case AssetMainType.Property:
                        setPropertyEdit(true);
                        break;
                    case AssetMainType.Vehicle:
                        setVehicleEdit(true);
                        break;
                    case AssetMainType.Debenture:
                        setDebentureEdit(true);
                        break;
                    case AssetMainType.Currency:
                        setCurrencyEdit(true);
                        break;
                    case AssetMainType.Cripto:
                        setCriptoEdit(true);
                        break;
                    case AssetMainType.InvestimentFunds:
                        setFundEdit(true);
                        break;
                    case AssetMainType.Stock:
                        setStockEdit(true);
                        break;
                }
            }
        },
        {
            label: 'Remover',
            icon: 'pi pi-times',
            color: "text-pink-600",
            command: async (id: string) => {
                trackAssetDeleteStarted(analyticsAssetType);
                removeAssets([id]);
            }
        }
    ];

    if (type == AssetMainType.Vehicle) {
        items.push({
            label: "Vender veículo",
            icon: "pi pi-dollar",
            color: "text-yellow-500",
            command: async (id: string) => {
                setSaleId(id);
            }
        })
    }

    if (type == AssetMainType.Property) {
        items.push({
            label: "Vender imóvel",
            icon: "pi pi-dollar",
            color: "text-yellow-500",
            command: async (id: string) => {
                setSaleId(id);
            }
        })
    }

    const removeAssetMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            toast.current?.show({ severity: 'info', summary: 'Aguarde', detail: `Removendo ativo${ids.length > 1 ? 's' : ''}` });

            await Promise.all(
                ids.map(id => {
                    if (type == AssetMainType.Vehicle)
                        return authHttp.doDelete(`/property/user/${id}`);
                    else if (type == AssetMainType.Property)
                        return authHttp.doDelete(`/property/user/${id}`);
                    else
                        return authHttp.doDelete(`/asset/${id}`);
                })
            );
        },
        onSuccess: () => {
            trackAssetDeleteCompleted(analyticsAssetType);
            toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: `Ativos removidos com sucesso` });
            onRefreshTable().then();
        },
        onError: () => {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: `Erro ao remover ativos` });
        }
    });

    const removeAssets = (ids: string[]) => {
        confirmDialog({
            header: `Remover ativo${ids.length > 1 ? 's' : ''}`,
            acceptLabel: "Sim",
            rejectLabel: "Não",
            message: `Após a remoção todo o histórico de rentabilidade desse ativo será removido permanentemente de sua carteira sem a possibilidade de recuperá-lo. Deseja continuar?`,
            accept: async () => {
                trackAssetDeleteConfirmed(analyticsAssetType);
                await removeAssetMutation.mutateAsync(ids);
            }
        });
    }

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                modal
                visible={detailsIsOpen}
                onHide={() => setDetailsIsOpen(false)}
                style={{ width: '80vw' }}
                header="Detalhes do ativo"
            >
                {
                    type == AssetMainType.Vehicle
                        ? <VehicleDetails id={editId} />
                        : type == AssetMainType.Property
                            ? <PropertyDetails id={editId} />
                            : <AssetDetails id={editId} />
                }
            </Dialog>

            <SaleForm 
                id={saleId ?? ""}
                visible={!!saleId}
                type={type}
                name={model.find(m => m.asset.id == saleId)?.asset.name ?? ""}
                toast={toast.current}
                onHide={() => setSaleId(null)}
                onRefreshTable={onRefreshTable}
            />

            {
                !model.length
                    ? (
                        <Container fullWidth center>
                            <Text>Nenhum ativo cadastrado</Text>
                        </Container>
                    )
                    : (
                        <Container flexColumn gap={3}>
                            <Container className="justify-content-end">
                                <Button
                                    outlined
                                    severity="danger"
                                    className="gap-1"
                                    icon="pi pi-trash"
                                    disabled={!selectedAssets.length || removeAssetMutation.isPending}
                                    loading={removeAssetMutation.isPending}
                                    onClick={() => {
                                        removeAssets(selectedAssets.map(a => a.asset.id));
                                    }}
                                >
                                    {removeAssetMutation.isPending ? "Removendo..." : "Remover ativos"}
                                </Button>
                            </Container>

                            <DataTable
                                value={model}
                                selectionMode="checkbox"
                                selection={selectedAssets}
                                onSelectionChange={(e) => setSelectedAssets(e.value)}
                                tableStyle={{ minWidth: '50rem' }}
                            >
                                <Column 
                                    selectionMode="multiple" 
                                    headerStyle={{ width: '3rem' }}
                                    frozen={!isTablet && !isMobile}
                                    alignFrozen="left"
                                />

                                {
                                    model.some((m) => !!(m as any).assetFiIconUrl || !!(m as any).assetIconUrl) && (
                                        <Column
                                            body={row => {
                                                return <img src={!row.assetFiIconUrl ? row.assetIconUrl : row.assetFiIconUrl} alt={row.name} width={24} height={24} />
                                            }}
                                        />
                                    )
                                }

                                <Column
                                    field="name"
                                    header="Nome"
                                    body={row => {
                                        const isProcessing = homeContext.processingAssetIds.includes(row.asset.id);
                                        const subtitle = getAssetListSubtitle(row);
                                        return (
                                            <Container gap={2} itemsCenter className="flex-nowrap">
                                                <AssetSourceIndicator sourceRaw={row.asset.source} />
                                                <Container flexColumn gap={1}>
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                        showEllipsis
                                                    >
                                                        {row.name}
                                                    </Text>
                                                    {!!subtitle && (
                                                        <Text
                                                            color="600"
                                                            size="sm"
                                                            fontWeight="normal"
                                                            showEllipsis
                                                        >
                                                            {subtitle}
                                                        </Text>
                                                    )}
                                                </Container>
                                                {
                                                    isProcessing && (
                                                        <Tag value="Atualizando" severity="warning" />
                                                    )
                                                }
                                            </Container>
                                        )
                                    }}
                                />

                                {
                                    model.some((m) => !!m.asset.purchaseDate) && (
                                        <Column
                                            field="purchaseDate"
                                            header="Data de compra"
                                            body={row => {
                                                return (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {moment(row.asset.purchaseDate).format("DD/MM/YYYY")}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    )
                                }

                                {
                                    model.some((m) => !!m.asset.dueDate) && (
                                        <Column
                                            field="dueDate"
                                            header="Data de vencimento"
                                            body={row => {
                                                return (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {moment(row.asset.dueDate).format("DD/MM/YYYY")}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    )
                                }

                                <Column
                                    field="investedValue"
                                    header="Valor investido"
                                    sortable
                                    body={row => {
                                        return (
                                            <Currency value={row.asset.investedValue}>
                                                {
                                                    (value) => (
                                                        <Text
                                                            color="900"
                                                            size="base"
                                                            fontWeight="normal"
                                                        >
                                                            {value}
                                                        </Text>
                                                    )
                                                }
                                            </Currency>
                                        );
                                    }}
                                />

                                {type == AssetMainType.Property && (
                                    <Column
                                        field="valuationValue"
                                        header="Valor estimado (IA)"
                                        sortable
                                        body={row => {
                                            const value = toCurrency(row.valuationValue);

                                            return (
                                                <Text
                                                    color="900"
                                                    size="base"
                                                    fontWeight="normal"
                                                >
                                                    {value.includes("NaN") ? "-" : value}
                                                </Text>
                                            );
                                        }}
                                    />
                                )}
                                
                                <Column
                                    field="currentAmount"
                                    header="Valor atual"
                                    sortable
                                    body={row => (
                                        <Currency value={row.asset.currentAmount}>
                                            {
                                                (value) => (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {value}
                                                    </Text>
                                                )
                                            }
                                        </Currency>
                                    )}
                                />

                                {
                                    model.some((m: any) => m.quantity) &&
                                        <Column
                                            field="quantity"
                                            header="Quantidade"
                                            style={{ maxWidth: '13rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            body={row => {
                                                return (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {row.quantity ?? "-"}
                                                    </Text>
                                                )
                                            }}
                                        />
                                }

                                {
                                    model.some((m: any) => m.averagePrice) &&
                                        <Column
                                            field="averagePrice"
                                            header="Preço médio"
                                            style={{ maxWidth: '13rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            body={row => {
                                                return (
                                                    <Currency value={row.averagePrice}>
                                                        {
                                                            (value) => (
                                                                <Text
                                                                    color="900"
                                                                    size="base"
                                                                    fontWeight="normal"
                                                                >
                                                                    {value}
                                                                </Text>
                                                            )
                                                        }
                                                    </Currency>
                                                )
                                            }}
                                        />
                                }

                                {
                                    model.some((m: any) => m.actualPrice) &&
                                        <Column
                                            field="actualPrice"
                                            header="Preço atual"
                                            style={{ maxWidth: '13rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            body={row => {
                                                return (
                                                    <Currency value={row.actualPrice}>
                                                        {
                                                            (value) => (
                                                                <Text
                                                                    color="900"
                                                                    size="base"
                                                                    fontWeight="normal"
                                                                >
                                                                    {value}
                                                                </Text>
                                                            )
                                                        }
                                                    </Currency>
                                                )
                                            }}
                                        />
                                }

                                {
                                    model.some((m: any) => !!m.issuingBank || !!m.issuingBankName) &&
                                        <Column
                                            header="Banco emissor"
                                            style={{ maxWidth: '13rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            body={row => {
                                                return (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {row.issuingBank ?? row.issuingBankName ?? "-"}
                                                    </Text>
                                                )
                                            }}
                                        />
                                }

                                {
                                    model.some((m: any) => !!m.negotiatingBankName) && (
                                        <Column
                                            field="negotiatingBankName"
                                            header="Onde comprou"
                                            style={{ minWidth: '13rem', maxWidth: "13rem", overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            body={row => {
                                                return (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {row.negotiatingBankName ?? "-"}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    )
                                }

                                <Column
                                    field="profit"
                                    header="Resultado"
                                    sortable
                                    body={row => (
                                        <Currency value={row.asset.profit}>
                                            {
                                                (value) => (
                                                    <Text
                                                        color="900"
                                                        size="base"
                                                        fontWeight="normal"
                                                    >
                                                        {value}
                                                    </Text>
                                                )
                                            }
                                        </Currency>
                                    )}
                                ></Column>
                                <Column
                                    field="profitPercentage"
                                    header="Posição atual"
                                    sortable
                                    body={row => (
                                        <PercentageText
                                            color="900"
                                            size="base"
                                            fontWeight="normal"
                                        >
                                            {row.asset.profitPercentage}
                                        </PercentageText>
                                    )}
                                ></Column>
                                <Column
                                    field="representWalletPercentage"
                                    header="% da Carteira"
                                    sortable
                                    body={row => (
                                        <PercentageText
                                            color="900"
                                            size="base"
                                            fontWeight="normal"
                                        >
                                            {row.asset.representWalletPercentage}
                                        </PercentageText>
                                    )}
                                ></Column>
                                <Column
                                    frozen={!isTablet && !isMobile}
                                    alignFrozen="right"
                                    body={row =>
                                        <>
                                            {
                                                items.map(item => {
                                                    return (
                                                        <>
                                                            <Tooltip 
                                                                target={`.${item.icon.split(" ")[item.icon.split(" ").length - 1]}`} 
                                                                content={item.label} 
                                                                position="left"
                                                            />
                                                            <Button
                                                                key={item.label}
                                                                type="button"
                                                                icon={item.icon}
                                                                className={`p-button-sm p-button-text ${item.color}`}
                                                                loading={(removeAssetMutation.isPending && item.label == "Remover")}
                                                                onClick={() => {
                                                                    item.command(row.asset.id);
                                                                }}
                                                            />
                                                        </>
                                                    )
                                                })
                                            }
                                        </>
                                    }
                                ></Column>
                            </DataTable>
                        </Container>
                    )
            }

            <Dialog style={{ width: "50rem" }} visible={privateBondsEdit} onHide={() => setPrivateBondsEdit(false)} modal>
                <PrivateBondsHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setPrivateBondsEdit(false);
                    }}
                    goBack={() => setPrivateBondsEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={savingAccountEdit} onHide={() => setSavingAccountEdit(false)} modal>
                <SavingAccountHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setSavingAccountEdit(false);
                    }}
                    goBack={() => setSavingAccountEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={treasureBoundsEdit} onHide={() => setTreasureBoundEdit(false)} modal>
                <TreasureBoundsHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setTreasureBoundEdit(false);
                    }}
                    goBack={() => setTreasureBoundEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={checkingAccountEdit} onHide={() => setCheckingAccountEdit(false)} modal>
                <CheckingAccoundHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setCheckingAccountEdit(false);
                    }}
                    goBack={() => setCheckingAccountEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={customizedEdit} onHide={() => setCustomizedEdit(false)} modal>
                <CustomizedHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setCustomizedEdit(false);
                        if (editId) {
                            toast.current?.show({
                                severity: "success",
                                summary: "Investimento atualizado com sucesso"
                            });
                        } else {
                            toast.current?.show({
                                severity: "success",
                                summary: "Investimento cadastrado com sucesso"
                            });
                        }
                    }}
                    goBack={() => setCustomizedEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={propertyEdit} onHide={() => setPropertyEdit(false)} modal>
                <PropertyHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setPropertyEdit(false);
                    }}
                    goBack={() => setPropertyEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={vehicleEdit} onHide={() => setVehicleEdit(false)} modal>
                <VehicleHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setVehicleEdit(false);
                    }}
                    goBack={() => setVehicleEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={debentureEdit} onHide={() => setDebentureEdit(false)} modal>
                <DebentureHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setDebentureEdit(false);
                    }}
                    goBack={() => setDebentureEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={currencyEdit} onHide={() => setCurrencyEdit(false)} modal>
                <CurrencyHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setCurrencyEdit(false);
                    }}
                    goBack={() => setCurrencyEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={criptoEdit} onHide={() => setCriptoEdit(false)} modal>
                <CriptoHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setCriptoEdit(false);
                    }}
                    goBack={() => setCriptoEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={fundEdit} onHide={() => setFundEdit(false)} modal>
                <FundHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setFundEdit(false);
                    }}
                    goBack={() => setFundEdit(false)}
                    assetId={editId}
                />
            </Dialog>

            <Dialog style={{ width: "50rem" }} visible={stockEdit} onHide={() => setStockEdit(false)} modal>
                <StockHandleForm
                    onSuccessRegister={() => {
                        trackAssetEditCompleted(analyticsAssetType);
                        setStockEdit(false);
                    }}
                    goBack={() => setStockEdit(false)}
                    assetId={editId}
                />
            </Dialog>
        </>
    );
}

export default function AssetManagementCard() {
    const homeContext = useHomeContext();

    return (
        <>
            <Card
                isLoading={homeContext.isLoading}
                title="Posição da carteira"
                subTitle="Acompanhe e edite seus ativos"
                showEmptyWallet={false}
            >
                <Container className="p-accordion-fixed-income" fullWidth>
                    <Accordion>
                        <AccordionTab
                            header={
                                <AccordionHeader
                                    title="Renda Fixa"
                                    subTitle={`${homeContext.fixedIncomeModel?.length ?? 0} ativos`}
                                    endContent={
                                        <AssetAccordionHeaderEndContent
                                            walletPercentage={homeContext.fixedIncomeModel.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                            totalPosition={homeContext.fixedIncomeModel.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                            result={homeContext.fixedIncomeModel.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                            rentability={homeContext.fixedIncomeModel.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                        />
                                    }
                                />
                            }
                        >
                            {
                                !homeContext.fixedIncomeGrouppedModel.length
                                    ? (
                                        <Container fullWidth center>
                                            <Text>Nenhum ativo cadastrado</Text>
                                        </Container>
                                    )
                                    : homeContext.fixedIncomeGrouppedModel.map((v, index) => {
                                        return (
                                            <Container key={index} className="p-accordion-fixed-income-light">
                                                <Accordion>
                                                    <AccordionTab
                                                        header={
                                                            <AccordionHeader
                                                                title={v[0].title}
                                                                subTitle={`${v.length} ativos`}
                                                                endContent={
                                                                    <AssetAccordionHeaderEndContent
                                                                        walletPercentage={v.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                                                        totalPosition={v.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                                                        result={v.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                                                        rentability={v.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                                                    />
                                                                }
                                                            />
                                                        }
                                                    >
                                                        <AssetTable
                                                            model={v}
                                                            type={v[0].mainType}
                                                            onRefreshTable={async () => { await homeContext.refresh(); }}
                                                        />
                                                    </AccordionTab>
                                                </Accordion>
                                            </Container>
                                        )
                                    })
                            }
                        </AccordionTab>
                    </Accordion>
                </Container>

                <Container className="p-accordion-variable-income" fullWidth>
                    <Accordion>
                        <AccordionTab
                            header={
                                <AccordionHeader
                                    title="Renda Variável"
                                    subTitle={`${homeContext.variableIncomeModel?.length ?? 0} ativos`}
                                    endContent={
                                        <AssetAccordionHeaderEndContent
                                            walletPercentage={homeContext.variableIncomeModel.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                            totalPosition={homeContext.variableIncomeModel.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                            result={homeContext.variableIncomeModel.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                            rentability={homeContext.variableIncomeModel.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                        />
                                    }
                                />
                            }
                        >
                            {
                                !homeContext.variableIncomeGrouppedModel.length
                                    ? (
                                        <Container fullWidth center>
                                            <Text>Nenhum ativo cadastrado</Text>
                                        </Container>
                                    )
                                    : homeContext.variableIncomeGrouppedModel.map((v, index) => {
                                        return (
                                            <Container key={index} className="p-accordion-variable-income-light">
                                                <Accordion>
                                                    <AccordionTab
                                                        header={
                                                            <AccordionHeader
                                                                title={v[0].title}
                                                                subTitle={`${v.length} ativos`}
                                                                endContent={
                                                                    <AssetAccordionHeaderEndContent
                                                                        walletPercentage={v.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                                                        totalPosition={v.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                                                        result={v.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                                                        rentability={v.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                                                    />
                                                                }
                                                            />
                                                        }
                                                    >
                                                        <AssetTable
                                                            onRefreshTable={async () => { await homeContext.refresh(); }}
                                                            model={v}
                                                            type={v[0].mainType}
                                                        />
                                                    </AccordionTab>
                                                </Accordion>
                                            </Container>
                                        )
                                    })
                            }
                        </AccordionTab>
                    </Accordion>
                </Container>

                <Container className="p-accordion-cashflow" fullWidth>
                    <Accordion>
                        <AccordionTab
                            header={
                                <AccordionHeader
                                    title="Em caixa"
                                    subTitle={`${homeContext.cashflowModel?.length ?? 0} ativos`}
                                    endContent={
                                        <AssetAccordionHeaderEndContent
                                            walletPercentage={homeContext.cashflowModel.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                            totalPosition={homeContext.cashflowModel.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                            result={homeContext.cashflowModel.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                            rentability={homeContext.cashflowModel.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                        />
                                    }
                                />
                            }
                        >
                            {
                                !homeContext.cashflowGrouppedModel.length
                                    ? (
                                        <Container fullWidth center>
                                            <Text>Nenhum ativo cadastrado</Text>
                                        </Container>
                                    )
                                    : homeContext.cashflowGrouppedModel.map((v, index) => {
                                        return (
                                            <Container key={index} className="p-accordion-cashflow-light">
                                                <Accordion>
                                                    <AccordionTab
                                                        header={
                                                            <AccordionHeader
                                                                title={v[0].title}
                                                                subTitle={`${v.length} ativos`}
                                                                endContent={
                                                                    <AssetAccordionHeaderEndContent
                                                                        walletPercentage={v.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                                                        totalPosition={v.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                                                        result={v.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                                                        rentability={v.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                                                    />
                                                                }
                                                            />
                                                        }
                                                    >
                                                        <AssetTable
                                                            onRefreshTable={async () => { await homeContext.refresh(); }}
                                                            model={v}
                                                            type={v[0].mainType}
                                                        />
                                                    </AccordionTab>
                                                </Accordion>
                                            </Container>
                                        )
                                    })
                            }
                        </AccordionTab>
                    </Accordion>
                </Container>

                <Container className="p-accordion-properties" fullWidth>
                    <Accordion>
                        <AccordionTab
                            header={
                                <AccordionHeader
                                    title="Imóveis"
                                    subTitle={`${homeContext.propertyModel?.length ?? 0} ativos`}
                                    endContent={
                                        <AssetAccordionHeaderEndContent
                                            walletPercentage={homeContext.propertyModel.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                            totalPosition={homeContext.propertyModel.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                            result={homeContext.propertyModel.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                            rentability={homeContext.propertyModel.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                        />
                                    }
                                />
                            }
                        >
                            <AssetTable
                                onRefreshTable={async () => { await homeContext.refresh(); }}
                                model={homeContext.propertyModel}
                                type={homeContext.propertyModel?.[0]?.mainType}
                            />
                        </AccordionTab>
                    </Accordion>
                </Container>

                <Container className="p-accordion-vehicles" fullWidth>
                    <Accordion>
                        <AccordionTab
                            header={
                                <AccordionHeader
                                    title="Veículos"
                                    subTitle={`${homeContext.vehicleModel?.length ?? 0} ativos`}
                                    endContent={
                                        <AssetAccordionHeaderEndContent
                                            walletPercentage={homeContext.vehicleModel.map(v => v.asset.representWalletPercentage).reduce((a, b) => a + b, 0)}
                                            totalPosition={homeContext.vehicleModel.map(v => v.asset.currentAmount).reduce((a, b) => a + b, 0)}
                                            result={homeContext.vehicleModel.map(v => v.asset.profit).reduce((a, b) => a + b, 0)}
                                            rentability={homeContext.vehicleModel.map(v => v.asset.profitPercentage).reduce((a, b) => a + b, 0)}
                                        />
                                    }
                                />
                            }
                        >
                            <AssetTable
                                onRefreshTable={async () => { await homeContext.refresh(); }}
                                model={homeContext.vehicleModel}
                                type={homeContext.vehicleModel?.[0]?.mainType}
                            />
                        </AccordionTab>
                    </Accordion>
                </Container>
            </Card>
        </>
    )
}
